import { Browser } from '@capacitor/browser';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { Amplify, API } from 'aws-amplify';

import { LocalForageStorage } from '../../common/classes/local-forage';
import { ATT_STATUS_STORAGE_KEY, ENDPOINT, IS_RUNNING_NATIVE_TESTS, IS_RUNNING_TESTS } from '../../common/constants';
import { ENV_TYPE_SHORTHAND } from '../../env-type';
import { getAppRedirectURI, IDP_COOKIE_CONSENT_QUERY_PARAM } from '../../features/auth/auth-helpers';
import { AWS_REGION, REGION_SHORTHAND } from '../../region-type';
import { getDynatraceAuthenticationFlowSource, trackDynatraceSignInCancelled } from '../native/dynatrace-events';

const IS_ANDROID = Capacitor.getPlatform() === 'android';
const NATIVE_TEST_MOCK_SERVER_URL = IS_ANDROID ? 'http://10.0.2.2:1080' : 'http://localhost:1080';
let signInBrowserListener: PluginListenerHandle | undefined;

export async function clearSignInBrowserListener() {
  const listener = signInBrowserListener;
  signInBrowserListener = undefined;
  await listener?.remove();
}

/**
 * Patch window.history.replaceState to handle SecurityError when custom URL schemes
 * (like schneidercharge://) are used. This prevents crashes during OAuth error handling
 * when Amplify tries to replace history with a custom scheme URL.
 *
 * Related Sentry issue: https://clipsal-solar.sentry.io/issues/6010118762/
 */
function patchHistoryForCustomSchemes() {
  if (typeof window === 'undefined' || !window.history) return;

  const originalReplaceState: History['replaceState'] = window.history.replaceState.bind(window.history);

  window.history.replaceState = function (...args: Parameters<History['replaceState']>) {
    const [data, unused, url] = args;

    try {
      return originalReplaceState(...args);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'SecurityError') {
        // Log the error but don't crash - this happens when Amplify tries to use
        // custom URL schemes (schneidercharge://) in web context during OAuth errors
        console.warn('Failed to replace history state with custom URL scheme:', url, error.message);
        // Try to replace with a safe fallback URL instead
        try {
          const fallbackUrl = window.location.pathname + window.location.search;
          return originalReplaceState(data, unused, fallbackUrl);
        } catch {
          // If even the fallback fails, just do nothing
          return;
        }
      }
      // Re-throw other types of errors
      throw error;
    }
  };
}

/**
 * Configures the AWS Amplify end-point setup for this repo.
 *
 * Note that we need to separately configure the `API` class, as for some reason in this monorepo architecture it does
 * not correctly initialize itself.
 *
 * see: https://github.com/aws-amplify/amplify-js/issues/5756
 */
export function setupAPI() {
  // Apply the history patch before configuring Amplify
  patchHistoryForCustomSchemes();
  const apiConfig = {
    endpoints: [
      {
        name: 'site',
        endpoint: IS_RUNNING_NATIVE_TESTS ? NATIVE_TEST_MOCK_SERVER_URL : ENDPOINT,
        region: AWS_REGION,
      },
    ],
  };

  const authOpts: Record<string, string | LocalForageStorage> = {
    region: AWS_REGION,
    userPoolId: import.meta.env?.[`VITE_${REGION_SHORTHAND}_${ENV_TYPE_SHORTHAND}_COGNITO_USER_POOL_ID`],
    userPoolWebClientId: import.meta.env?.[`VITE_${REGION_SHORTHAND}_${ENV_TYPE_SHORTHAND}_COGNITO_CLIENT_ID`],
  };

  // Don't use indexedDB in Cypress tests since it causes flakes
  if (!IS_RUNNING_TESTS) {
    authOpts.storage = new LocalForageStorage();
  }

  Amplify.configure({
    Auth: authOpts,
    API: apiConfig,
    oauth: {
      domain: `${
        ENV_TYPE_SHORTHAND === 'STAGING' ? 'clippy-staging' : 'clippy-prod'
      }.auth.${AWS_REGION}.amazoncognito.com`,
      // Uncomment this when working in preview environments
      // domain: `clippy-preview.auth.ap-southeast-2.amazoncognito.com`,
      scope: ['openid', 'profile', 'email', 'aws.cognito.signin.user.admin'],
      redirectSignIn: getAppRedirectURI(true),
      redirectSignOut: getAppRedirectURI(true),
      responseType: 'code',
      urlOpener: async (url: string) => {
        const loginUrl = new URL(url);
        const pendingAttStatus = sessionStorage.getItem(ATT_STATUS_STORAGE_KEY);
        if (pendingAttStatus) {
          const isAttAuthorized = pendingAttStatus === 'authorized';
          loginUrl.searchParams.set(IDP_COOKIE_CONSENT_QUERY_PARAM, isAttAuthorized.toString());
        }

        await clearSignInBrowserListener();
        if (Capacitor.getPlatform() !== 'web' && getDynatraceAuthenticationFlowSource() === 'login') {
          signInBrowserListener = await Browser.addListener('browserFinished', () => {
            if (!signInBrowserListener) return;
            if (getDynatraceAuthenticationFlowSource() === 'login') trackDynatraceSignInCancelled();
            void clearSignInBrowserListener();
          });
        }
        try {
          await Browser.open({ url: loginUrl.toString(), windowName: '_self' });
        } catch (error) {
          await clearSignInBrowserListener();
          throw error;
        }
      },
    },
  });
  API.configure(apiConfig);
}
